import React, { useState } from 'react';
import BomSettingsForm from '../bom/settings/BomSettingsForm';
import SbomSettingsForm from '../sbom/SbomSettingsForm';
import CollabSettingsForm from '../components/CollabSettingsForm';
import '../bom/bom-design-system.css';
import '../styles/AdminSettings.css';

/**
 * Admin → Settings — tenant-level configuration, reached from the user menu.
 *
 * One TAB per module that HAS tenant settings; a module the account isn't licensed for
 * contributes no tab (and with a single tab left the bar is not drawn at all — a lone tab
 * is a heading pretending to be a control). Only the active tab is mounted, so each form
 * fetches when it is first opened, not on page load.
 *
 * The route is admin-only in App.jsx, and every save is re-checked on the server, so this
 * page is a convenience, not the gate.
 */
export default function AdminSettings({ hasBom, hasSbom, account }) {
  const tabs = [
    // Collaboration belongs to the PLATFORM, not to a module, so it is shown for every
    // tenant — there is no licence to check.
    { key: 'collab', label: 'Collaboration', render: () => <CollabSettingsForm /> },
  ];
  // The Software BOM has its own tab and its own settings row — saving it never touches the
  // hardware BOM's (v4.30.0).
  if (hasSbom) {
    tabs.unshift({
      key: 'sbom',
      label: 'Software BOM',
      className: 'bom-scope',
      render: () => <SbomSettingsForm />,
    });
  }
  if (hasBom) {
    tabs.unshift({
      key: 'bom',
      label: 'BOM Viewer',
      className: 'bom-scope',
      render: () => <BomSettingsForm />,
    });
  }

  const [active, setActive] = useState(tabs[0].key);
  const current = tabs.find((t) => t.key === active) || tabs[0];

  return (
    <div className="admin-settings">
      <div className="admin-settings-head">
        <div className="admin-settings-crumb">Admin</div>
        <h1>Settings</h1>
        {account && <p className="admin-settings-sub">For tenant {account}</p>}
      </div>

      {tabs.length > 1 && (
        <div className="admin-settings-tabs" role="tablist" aria-label="Settings sections">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`admin-settings-tab-${t.key}`}
              aria-selected={current.key === t.key}
              aria-controls={`admin-settings-panel-${t.key}`}
              className={`admin-settings-tab${current.key === t.key ? ' active' : ''}`}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <section
        role="tabpanel"
        id={`admin-settings-panel-${current.key}`}
        aria-labelledby={`admin-settings-tab-${current.key}`}
        className={`admin-settings-section${current.className ? ` ${current.className}` : ''}`}
      >
        {current.render()}
      </section>
    </div>
  );
}
